/**
 * Creates a debounced function that delays invoking func until after wait milliseconds
 * have elapsed since the last time the debounced function was invoked.
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout | null = null
  
  return function debounced(...args: Parameters<T>) {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    
    timeoutId = setTimeout(() => {
      func(...args)
      timeoutId = null
    }, wait)
  }
}

/**
 * Creates a debounced callback manager for Svelte components
 */
export class DebouncedCallback {
  private timer: NodeJS.Timeout | null = null
  
  constructor(
    private callback: (value: any) => void,
    private delay: number = 500
  ) {}
  
  trigger(value: any) {
    if (this.timer) {
      clearTimeout(this.timer)
    }
    
    if (this.delay > 0) {
      this.timer = setTimeout(() => {
        this.callback(value)
        this.timer = null
      }, this.delay)
    } else {
      this.callback(value)
    }
  }
  
  cancel() {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }
  
  destroy() {
    this.cancel()
  }
}